import { useMemo, useEffect, useState } from 'react';
import { useGetEntity, useQueryByPath } from './use-entities';

export interface BreadcrumbItem {
  parentId: string;
  name?: string;
  isLoading?: boolean;
}

interface Entity {
  entityId: string;
  name: string;
  path: string;
  parentId?: string;
}

interface UseBreadcrumbsOptions {
  entity?: Entity;
  currentEntityId?: string; // For backward compatibility when entity is not provided
}

export function useBreadcrumbs({ entity, currentEntityId }: UseBreadcrumbsOptions) {
  // Fetch entity if not provided but currentEntityId is given
  const { data: fetchedEntity, isLoading } = useGetEntity(currentEntityId || '', {
    enabled: !entity && !!(currentEntityId && currentEntityId !== 'root')
  });

  const [pathEntityMap, setPathEntityMap] = useState<Record<string, string>>({});
  const queryByPathMutation = useQueryByPath();

  const activeEntity = entity || fetchedEntity;

  // Resolve entity IDs for path segments
  useEffect(() => {
    if (!activeEntity?.path) {
      setPathEntityMap({});
      return;
    }

    const pathSegments = activeEntity.path.split('/').filter((segment) => segment.trim() !== '');

    if (pathSegments.length <= 2) {
      setPathEntityMap({});
      return; // No need to resolve if only root and current
    }

    const resolvePathSegments = async () => {
      const entityMap: Record<string, string> = {};

      // Build cumulative paths and resolve each one
      for (let i = 0; i < pathSegments.length - 1; i++) {
        const partialPath = '/' + pathSegments.slice(0, i + 1).join('/') + '/';

        try {
          const result = await queryByPathMutation.mutateAsync({ path: partialPath });
          if (result.entity) {
            entityMap[partialPath] = result.entity.entityId;
          }
        } catch (error) {
          console.warn(`Failed to resolve path ${partialPath}:`, error);
          // Keep the fallback behavior for failed resolutions
          entityMap[partialPath] = 'root';
        }
      }

      setPathEntityMap(entityMap);
    };

    resolvePathSegments();
  }, [activeEntity?.path]);

  return useMemo(() => {
    const breadcrumbs: BreadcrumbItem[] = [];

    // Always start with Root
    breadcrumbs.push({
      parentId: 'root'
    });

    if (activeEntity?.path) {
      // Parse the path to extract individual segments
      // Path format: "/Test-1/Child 2/Child 3/"
      const pathSegments = activeEntity.path.split('/').filter((segment) => segment.trim() !== '');

      // Build breadcrumbs from path segments
      pathSegments.forEach((segment, index) => {
        let navigationId: string;

        if (index === pathSegments.length - 1) {
          // Last segment - this is the current entity
          navigationId = activeEntity.entityId;
        } else if (index === pathSegments.length - 2 && activeEntity.parentId) {
          // Second to last segment - parent entity
          navigationId = activeEntity.parentId;
        } else {
          // Earlier segments - resolve from pathEntityMap or fallback to root
          const partialPath = '/' + pathSegments.slice(0, index + 1).join('/') + '/';
          navigationId = pathEntityMap[partialPath] ?? 'root';
        }

        breadcrumbs.push({
          parentId: navigationId,
          name: segment
        });
      });
    }

    const combinedLoading = isLoading || queryByPathMutation.isPending;
    return { breadcrumbs, isLoading: combinedLoading };
  }, [entity, fetchedEntity, isLoading, pathEntityMap, queryByPathMutation.isPending]);
}
